import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import DropDown from './DropDown';
import { User, LogOut, Settings } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

export default function Header({
  logoUrl,
  colorVariant,
  cart,
  actionBtnText,
  actionBtnUrl,
}) {
  const [isSticky, setIsSticky] = useState(false);
  const [mobileToggle, setMobileToggle] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [currentUser, setCurrentUser] = useState(null);
  
  useEffect(() => {
    setDropdownOpen(false);
  }, [location.pathname]);
  
  useEffect(() => {
    const checkUser = () => {
      const userData = localStorage.getItem('user');
      if (userData) {
        setCurrentUser(JSON.parse(userData));
      } else {
        setCurrentUser(null);
      }
    };
    
    checkUser();
    const interval = setInterval(checkUser, 500);
    
    return () => clearInterval(interval);
  }, []);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const handleLogout = () => {
    logout();
    navigate('/');
  };
  
  const handleSectionClick = (sectionId) => {
    setMobileToggle(false);
    if (location.pathname === '/') {
      // If on home page, scroll to section
      if (sectionId === 'home') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // If on other page, navigate to home then scroll
      navigate('/');
      setTimeout(() => {
        if (sectionId === 'home') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
      
      // Check if at top for home
      const homeThreshold = window.innerWidth < 768 ? 100 : 200;
      if (window.scrollY < homeThreshold) {
        setActiveSection('home');
      }
    };
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && window.scrollY >= 50) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { 
        threshold: 0, 
        rootMargin: '-20% 0px -50% 0px'
      }
    );
    
    const sections = ['benefits', 'about', 'key-ingredients', 'comparison', 'how-to-use', 'product-section', 'reviews', 'contact'];
    sections.forEach((id) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });
    
    // Set initial state
    const initialThreshold = window.innerWidth < 768 ? 100 : 200;
    if (window.scrollY < initialThreshold) {
      setActiveSection('home');
    }
    
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, [location.pathname]);
  return (
    <header
      className={`cs_site_header cs_style_1 cs_sticky_header ${
        colorVariant ? colorVariant : 'cs_primary_color'
      } ${isSticky ? '' : ''}`}
      // } ${isSticky ? 'cs_gescout_show' : ''}`}
    >
      <div className="cs_main_header">
        <div className="container">
          <div className="cs_main_header_in">
            <div className="cs_main_header_left">
              <Link 
                className="cs_site_branding" 
                to="/"
                onClick={(e) => {
                  e.preventDefault();
                  handleSectionClick('home');
                }}
              >
                <img src={logoUrl} alt="Logo" />
              </Link>
            </div>
            <div className="cs_main_header_center">
              <div className="cs_nav cs_medium cs_primary_font">
                <ul
                  className={`${
                    mobileToggle ? 'cs_nav_list cs_active' : 'cs_nav_list'
                  }`}
                >
                  <li>
                    <a href="#" style={activeSection === 'home' && location.pathname === '/' ? {color: '#0b5e4b', fontWeight: 'bold'} : {}} onClick={(e) => {
                      e.preventDefault();
                      handleSectionClick('home');
                    }}>Home</a>
                  </li>
                  <li>
                    <a href="#benefits" style={activeSection === 'benefits' && location.pathname === '/' ? {color: '#0b5e4b', fontWeight: 'bold'} : {}} onClick={(e) => {
                      e.preventDefault();
                      handleSectionClick('benefits');
                    }}>Benefits</a>
                  </li>
                  <li>
                    <a href="#about" style={activeSection === 'about' && location.pathname === '/' ? {color: '#0b5e4b', fontWeight: 'bold'} : {}} onClick={(e) => {
                      e.preventDefault();
                      handleSectionClick('about');
                    }}>About</a>
                  </li>
                  <li>
                    <a href="#key-ingredients" style={activeSection === 'key-ingredients' && location.pathname === '/' ? {color: '#0b5e4b', fontWeight: 'bold'} : {}} onClick={(e) => {
                      e.preventDefault();
                      handleSectionClick('key-ingredients');
                    }}>Ingredients</a>
                  </li>
                  <li>
                    <a href="#comparison" style={activeSection === 'comparison' && location.pathname === '/' ? {color: '#0b5e4b', fontWeight: 'bold'} : {}} onClick={(e) => {
                      e.preventDefault();
                      handleSectionClick('comparison');
                    }}>Comparison</a>
                  </li>
                  <li>
                    <a href="#how-to-use" style={activeSection === 'how-to-use' && location.pathname === '/' ? {color: '#0b5e4b', fontWeight: 'bold'} : {}} onClick={(e) => {
                      e.preventDefault();
                      handleSectionClick('how-to-use');
                    }}>How to Use</a>
                  </li>
                  <li>
                    <a href="#product-section" style={activeSection === 'product-section' && location.pathname === '/' ? {color: '#0b5e4b', fontWeight: 'bold'} : {}} onClick={(e) => {
                      e.preventDefault();
                      handleSectionClick('product-section');
                    }}>Product</a>
                  </li>
                  <li>
                    <a href="#reviews" style={activeSection === 'reviews' && location.pathname === '/' ? {color: '#0b5e4b', fontWeight: 'bold'} : {}} onClick={(e) => {
                      e.preventDefault();
                      handleSectionClick('reviews');
                    }}>Reviews</a>
                  </li>
                  <li>
                    <a href="#contact" style={activeSection === 'contact' && location.pathname === '/' ? {color: '#0b5e4b', fontWeight: 'bold'} : {}} onClick={(e) => {
                      e.preventDefault();
                      handleSectionClick('contact');
                    }}>Contact</a>
                  </li>
                  {/* <li>
                    <Link to="/contact" style={location.pathname === '/contact' ? {color: '#0b5e4b', fontWeight: 'bold'} : {}}>Contact</Link>
                  </li> */}
                </ul>
                <span
                  className={
                    mobileToggle
                      ? 'cs_menu_toggle cs_teggle_active'
                      : 'cs_menu_toggle'
                  }
                  onClick={() => setMobileToggle(!mobileToggle)}
                >
                  <span></span>
                </span>
              </div>
            </div>
            {/* <div className="cs_main_header_right">
              {cart && (
                <a href="shop-cart" className="cs_header_cart">
                  <svg
                    width={22}
                    height={22}
                    viewBox="0 0 22 22"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g clipPath="url(#clip0_307_180)">
                      <path
                        d="M19.25 5.5H16.5C16.5 4.04131 15.9205 2.64236 14.8891 1.61091C13.8576 0.579463 12.4587 0 11 0C9.54131 0 8.14236 0.579463 7.11091 1.61091C6.07946 2.64236 5.5 4.04131 5.5 5.5H2.75C2.02065 5.5 1.32118 5.78973 0.805456 6.30546C0.289731 6.82118 0 7.52065 0 8.25L0 17.4167C0.00145554 18.6318 0.484808 19.7967 1.34403 20.656C2.20326 21.5152 3.3682 21.9985 4.58333 22H17.4167C18.6318 21.9985 19.7967 21.5152 20.656 20.656C21.5152 19.7967 21.9985 18.6318 22 17.4167V8.25C22 7.52065 21.7103 6.82118 21.1945 6.30546C20.6788 5.78973 19.9793 5.5 19.25 5.5ZM11 1.83333C11.9725 1.83333 12.9051 2.21964 13.5927 2.90728C14.2804 3.59491 14.6667 4.52754 14.6667 5.5H7.33333C7.33333 4.52754 7.71964 3.59491 8.40728 2.90728C9.09491 2.21964 10.0275 1.83333 11 1.83333ZM20.1667 17.4167C20.1667 18.146 19.8769 18.8455 19.3612 19.3612C18.8455 19.8769 18.146 20.1667 17.4167 20.1667H4.58333C3.85399 20.1667 3.15451 19.8769 2.63879 19.3612C2.12306 18.8455 1.83333 18.146 1.83333 17.4167V8.25C1.83333 8.00688 1.92991 7.77373 2.10182 7.60182C2.27373 7.42991 2.50688 7.33333 2.75 7.33333H5.5V9.16667C5.5 9.40978 5.59658 9.64294 5.76849 9.81485C5.94039 9.98676 6.17355 10.0833 6.41667 10.0833C6.65978 10.0833 6.89294 9.98676 7.06485 9.81485C7.23676 9.64294 7.33333 9.40978 7.33333 9.16667V7.33333H14.6667V9.16667C14.6667 9.40978 14.7632 9.64294 14.9352 9.81485C15.1071 9.98676 15.3402 10.0833 15.5833 10.0833C15.8264 10.0833 16.0596 9.98676 16.2315 9.81485C16.4034 9.64294 16.5 9.40978 16.5 9.16667V7.33333H19.25C19.4931 7.33333 19.7263 7.42991 19.8982 7.60182C20.0701 7.77373 20.1667 8.00688 20.1667 8.25V17.4167Z"
                        fill="currentColor"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_307_180">
                        <rect width={22} height={22} fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                  <span className="cs_header_cart_label">0</span>
                </a>
              )}
              <div className="cs_auth_buttons">
                {currentUser ? (
                  <div className="cs_user_dropdown" ref={dropdownRef}>
                    <button
                      className="cs_user_btn"
                      onClick={() => setDropdownOpen(!dropdownOpen)}
                    >
                      <User size={18} />
                      <span>{currentUser.name}</span>
                    </button>
                    {dropdownOpen && (
                      <div className="cs_dropdown_menu ">
                        <button className="cs_dropdown_item" onClick={() => {setMobileToggle(!mobileToggle); navigate('/profile')}}>
                          <Settings size={16} />
                          Profile
                        </button>
                        <button className="cs_dropdown_item" onClick={handleLogout}>
                          <LogOut size={16} />
                          Logout
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <Link 
                      to="/signin" 
                      className="cs_auth_btn cs_signin_btn" 
                      style={{
                        textDecoration: 'none', 
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer'
                      }}
                    >
                      Sign In
                    </Link>
                    <Link 
                      to="/signup" 
                      className="cs_auth_btn cs_signup_btn" 
                      style={{
                        textDecoration: 'none', 
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer'
                      }}
                    >
                      Sign Up
                    </Link>
                  </>
                )}

              </div>
              <style jsx>{`
                .cs_auth_buttons {
                  display: flex;
                  gap: 10px;
                }
                .cs_auth_btn {
                  padding: 10px 20px;
                  border-radius: 30px;
                  cursor: pointer;
                  transition: all 0.3s ease;
                  font-size: 14px;
                  font-weight: 500;
                  white-space: nowrap;
                  border: 1px solid #0b5e4b;
                  position: relative;
                  z-index: 10;
                  width: 100%;
                  height: 100%;
                  box-sizing: border-box;
                }
                .cs_signin_btn {
                  background: transparent;
                  color: #0b5e4b;
                }
                .cs_signin_btn:hover {
                  background: #0b5e4b;
                  color: white;
                }
                .cs_signin_btn:active {
                  transform: scale(0.95);
                  background: #00c47d;
                  color: white;
                }
                .cs_signup_btn {
                  background: #0b5e4b;
                  color: white;
                }
                .cs_signup_btn:hover {
                  background: transparent;
                  color: #0b5e4b;
                }
                .cs_signup_btn:active {
                  transform: scale(0.95);
                  background: #f0f0f0;
                  color: #0b5e4b;
                }
                .cs_user_dropdown {
                  position: relative;
                }
                .cs_user_btn {
                  display: flex;
                  align-items: center;
                  gap: 8px;
                  padding: 10px 15px;
                  background: #0b5e4b;
                  color: white;
                  border: none;
                  border-radius: 30px;
                  cursor: pointer;
                  font-size: 14px;
                  font-weight: 500;
                  transition: all 0.3s ease;
                }
                .cs_user_btn:hover {
                  background: #00c47d;
                }
                .cs_dropdown_menu {
                  position: absolute;
                  top: 100%;
                  right: 0;
                  background: white;
                  border: 1px solid #0b5e4b;
                  border-radius: 15px;
                  box-shadow: 0 8px 25px rgba(0, 218, 141, 0.15);
                  min-width: 150px;
                  z-index: 1000;
                  margin-top: 8px;
                  overflow: hidden;
                }
                .cs_dropdown_item {
                  display: flex;
                  align-items: center;
                  gap: 8px;
                  width: 100%;
                  padding: 12px 16px;
                  background: none;
                  border: none;
                  text-align: left;
                  cursor: pointer;
                  font-size: 14px;
                  color: #333;
                  transition: all 0.3s ease;
                  font-weight: 500;
                }
                .cs_dropdown_item:hover {
                  background: #0b5e4b;
                  color: white;
                }
                @media (max-width: 768px) {
                  .cs_auth_buttons {
                    gap: 5px;
                  }
                  .cs_auth_btn {
                    padding: 6px 12px;
                    font-size: 12px;
                  }
                  .cs_user_btn {
                    padding: 8px 12px;
                    font-size: 12px;
                    gap: 6px;
                  }
                  .cs_user_btn span {
                    max-width: 80px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                  }
                  .cs_dropdown_menu {
                    min-width: 120px;
                  }
                }
                @media (hover: none) and (pointer: coarse) {
                  .cs_auth_btn:hover {
                    background: revert;
                    color: revert;
                  }
                  .cs_signin_btn:hover {
                    background: transparent;
                    color: #0b5e4b;
                  }
                  .cs_signup_btn:hover {
                    background: #0b5e4b;
                    color: white;
                  }
                  .cs_auth_btn:active {
                    transform: scale(0.95);
                  }
                  .cs_signin_btn:active {
                    background: #0b5e4b;
                    color: white;
                  }
                  .cs_signup_btn:active {
                    background: transparent;
                    color: #0b5e4b;
                  }
                }
                @media (max-width: 480px) {
                  .cs_auth_btn {
                    padding: 5px 8px;
                    font-size: 11px;
                  }
                  .cs_user_btn {
                    padding: 6px 8px;
                    font-size: 11px;
                    gap: 4px;
                  }
                  .cs_user_btn span {
                    max-width: 60px;
                  }
                  .cs_dropdown_menu {
                    min-width: 100px;
                  }
                  .cs_dropdown_item {
                    padding: 10px 12px;
                    font-size: 12px;
                  }
                }
              `}</style>
            </div> */}
          </div>
        </div>
      </div>
    </header>
  );
}
